import React from "react";
import styled from "@emotion/styled";
import Spinner from "./Spinner";
import {
  BUTTON_BOX_SHADOW,
  BUTTON_PRIMARY_BORDER,
  GHOST_BACKGROUND,
  GHOST_BACKGROUND_PRESSED,
  TERTIARY_GREY,
  QUATERNARY_GREY,
  DISABLED_COLOR,
  DISABLED_BACKGROUND,
  OLD_BLUE,
  DELETE,
  DELETE_PRESSED,
  LIGHT_FONT,
} from "src/constants/colors";
import { PRIMARY_BG_GRADIENT } from "src/constants/styles";

export interface ButtonProps {
  width?: string;
  height?: string;
  padding?: string;
  fontSize?: string;
  variant?: "primary" | "secondary" | "ghost" | "delete";
  size?: "large" | "auto";
  initialBg?: boolean;
  loading?: boolean;
  disabled?: boolean;
  selected?: boolean;
  children: React.ReactNode;
  onClick?: () => void;
}

const shouldForwardProp = (prop: string) =>
  !["loading", "initialBg", "variant", "size", "selected"].includes(prop);

export const ButtonBase = styled("button", { shouldForwardProp })<ButtonProps>`
  width: ${({ width }) => (width ? width : "auto")};
  height: ${({ size }) => (size === "large" ? "48px" : "auto")};
  font-size: 16px;
  display: flex;
  flex-shrink: 0;
  gap: 8px;
  align-items: center;
  justify-content: center;
  border-radius: 40px;
  padding: 8px 14px 8px 14px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: background-color 0.2s linear;
`;

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  width?: string;
  height?: string;
  padding?: string;
  fontSize?: string;
  variant?: "primary" | "secondary" | "ghost" | "delete";
  size?: "large" | "auto";
  initialBg?: boolean;
  loading?: boolean;
  disabled?: boolean;
  selected?: boolean;
  children: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      size = "auto",
      width = "auto",
      initialBg = false,
      loading,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    return (
      <ButtonBase
        ref={ref}
        type="button"
        size={size}
        width={width}
        initialBg={initialBg}
        disabled={disabled}
        loading={loading}
        {...props}
      >
        {loading ? <Spinner /> : children}
      </ButtonBase>
    );
  },
);

export const PrimaryButton = styled(Button)<ButtonProps>`
  border: ${({ disabled, loading }) =>
    disabled || loading ? TERTIARY_GREY : `1px solid ${BUTTON_PRIMARY_BORDER}`};
  background: ${({ loading }) =>
    loading ? TERTIARY_GREY : PRIMARY_BG_GRADIENT};
  color: white;
  box-shadow: inset 0px 1px 0px 1px ${BUTTON_BOX_SHADOW};

  ${({ disabled }) =>
    !disabled &&
    `
      &:hover, &:active {
        outline: 4px solid ${DISABLED_BACKGROUND};
      }
    `}
`;

export const SecondaryButton = styled(Button)<ButtonProps>`
  background-color: rgba(239, 242, 246, 1);
  border: 0;
  color: ${({ disabled }) => (disabled ? DISABLED_COLOR : QUATERNARY_GREY)};
  font-size: 14px;

  ${({ disabled }) =>
    !disabled &&
    `
      &:hover {
        color: ${OLD_BLUE};
        background-color: ${GHOST_BACKGROUND};
      }
      &:active {
        color: ${OLD_BLUE};
        background-color: ${GHOST_BACKGROUND_PRESSED};
      }
    `}
`;

export const GhostButton = styled(SecondaryButton)<ButtonProps>`
  background-color: ${({ loading }) =>
    loading ? TERTIARY_GREY : "transparent"};

  ${({ selected }) =>
    selected &&
    `
      color: ${OLD_BLUE};
      background-color: ${GHOST_BACKGROUND};
    `}
`;

export const DeleteButton = styled(Button)<ButtonProps>`
  gap: 6px;
  color: ${({ initialBg }) => (initialBg ? LIGHT_FONT : DELETE)};
  border: 0;
  font-size: 14px;
  background: ${({ initialBg }) => (initialBg ? DELETE : "transparent")};

  &:hover {
    background: ${({ initialBg }) => (initialBg ? DELETE_PRESSED : DELETE)};
    color: ${({ initialBg }) => !initialBg && LIGHT_FONT};
  }

  &:active {
    background: ${DELETE_PRESSED};
  }
`;
